/**
 * Drapeaux de visibilité des menus / routes « dashboard établissement ».
 * Stockés sur l’institution (`dashboardMenuFlags`). Absence de clé ou valeur !== false → activé.
 */

export const INSTITUTION_MENU_FLAGS = {
  institution_panel: 'institution_panel',
  webinars_enterprise: 'webinars_enterprise',
  rh_campaigns: 'rh_campaigns',
  rh_codir: 'rh_codir',
  rh_competencies: 'rh_competencies',
  rh_requests: 'rh_requests',
  rh_catalog: 'rh_catalog',
  integrations_page: 'integrations_page',
  tab_webinar_settings: 'tab_webinar_settings',
  tab_rh_dashboard: 'tab_rh_dashboard',
  tab_infpc: 'tab_infpc',
  tab_flows: 'tab_flows',
  tab_trash: 'tab_trash',
  tab_supports: 'tab_supports',
  tab_categories: 'tab_categories',
  tab_templates: 'tab_templates',
  tab_content_pipeline: 'tab_content_pipeline',
  tab_designers: 'tab_designers',
  tab_learners: 'tab_learners',
  tab_learner_analytics: 'tab_learner_analytics',
  tab_bi_studio: 'tab_bi_studio',
  tab_classes: 'tab_classes',
  tab_evaluations: 'tab_evaluations',
  tab_training_sessions: 'tab_training_sessions',
  tab_groups: 'tab_groups',
  tab_services: 'tab_services',
  tab_branding: 'tab_branding',
  tab_landing: 'tab_landing',
  tab_webhooks: 'tab_webhooks',
  tab_scorm: 'tab_scorm',
  tab_xapi: 'tab_xapi',
  tab_mcp: 'tab_mcp',
  tab_sirh: 'tab_sirh',
  /**
   * Éducation uniquement : afficher l’onglet « Formations publiques » aux élèves (désactivé par défaut,
   * voir `MENU_FLAGS_DEFAULT_OFF`). Sans effet en entreprise.
   */
  learner_public_formations_education: 'learner_public_formations_education',
} as const;

export type InstitutionMenuFlagKey = (typeof INSTITUTION_MENU_FLAGS)[keyof typeof INSTITUTION_MENU_FLAGS];

export const ALL_INSTITUTION_MENU_FLAG_KEYS = Object.values(INSTITUTION_MENU_FLAGS) as InstitutionMenuFlagKey[];

/**
 * Regroupement pour la fiche établissement (admin) : facilite l’activation / désactivation
 * (ex. onglet INFPC à côté du pilotage RH).
 */
export type InstitutionMenuFlagAdminSection = { title: string; keys: readonly InstitutionMenuFlagKey[] };

const INSTITUTION_MENU_FLAG_ADMIN_GROUPS: readonly InstitutionMenuFlagAdminSection[] = [
  {
    title: 'Navigation & modules Formation / RH',
    keys: [
      INSTITUTION_MENU_FLAGS.institution_panel,
      INSTITUTION_MENU_FLAGS.webinars_enterprise,
      INSTITUTION_MENU_FLAGS.integrations_page,
      INSTITUTION_MENU_FLAGS.rh_campaigns,
      INSTITUTION_MENU_FLAGS.rh_codir,
      INSTITUTION_MENU_FLAGS.rh_competencies,
      INSTITUTION_MENU_FLAGS.rh_requests,
      INSTITUTION_MENU_FLAGS.rh_catalog,
    ],
  },
  {
    title: 'Onglets /institution — Général',
    keys: [INSTITUTION_MENU_FLAGS.tab_rh_dashboard, INSTITUTION_MENU_FLAGS.tab_infpc],
  },
  {
    title: 'Onglets — Contenu & formations',
    keys: [
      INSTITUTION_MENU_FLAGS.tab_flows,
      INSTITUTION_MENU_FLAGS.tab_webinar_settings,
      INSTITUTION_MENU_FLAGS.tab_trash,
      INSTITUTION_MENU_FLAGS.tab_supports,
      INSTITUTION_MENU_FLAGS.tab_categories,
      INSTITUTION_MENU_FLAGS.tab_templates,
      INSTITUTION_MENU_FLAGS.tab_content_pipeline,
    ],
  },
  {
    title: 'Onglets — Équipes & apprenants',
    keys: [
      INSTITUTION_MENU_FLAGS.tab_designers,
      INSTITUTION_MENU_FLAGS.tab_learners,
      INSTITUTION_MENU_FLAGS.tab_learner_analytics,
      INSTITUTION_MENU_FLAGS.tab_bi_studio,
      INSTITUTION_MENU_FLAGS.tab_classes,
      INSTITUTION_MENU_FLAGS.tab_evaluations,
      INSTITUTION_MENU_FLAGS.tab_training_sessions,
      INSTITUTION_MENU_FLAGS.tab_groups,
      INSTITUTION_MENU_FLAGS.tab_services,
      INSTITUTION_MENU_FLAGS.learner_public_formations_education,
    ],
  },
  {
    title: 'Onglets — Portail & image',
    keys: [INSTITUTION_MENU_FLAGS.tab_branding, INSTITUTION_MENU_FLAGS.tab_landing],
  },
  {
    title: 'Onglets — Intégrations',
    keys: [
      INSTITUTION_MENU_FLAGS.tab_webhooks,
      INSTITUTION_MENU_FLAGS.tab_scorm,
      INSTITUTION_MENU_FLAGS.tab_xapi,
      INSTITUTION_MENU_FLAGS.tab_mcp,
      INSTITUTION_MENU_FLAGS.tab_sirh,
    ],
  },
] as const;

/** Sections pour l’UI admin (toutes les clés couvertes + filet de sécurité si nouvelle clé oubliée dans les groupes). */
export function getInstitutionMenuFlagsAdminSections(): InstitutionMenuFlagAdminSection[] {
  const seen = new Set<InstitutionMenuFlagKey>();
  const out: InstitutionMenuFlagAdminSection[] = [];
  for (const g of INSTITUTION_MENU_FLAG_ADMIN_GROUPS) {
    const keys = g.keys.filter((k) => {
      seen.add(k);
      return true;
    });
    out.push({ title: g.title, keys });
  }
  const missing = ALL_INSTITUTION_MENU_FLAG_KEYS.filter((k) => !seen.has(k));
  if (missing.length > 0) {
    out.push({ title: 'Autres (non classés)', keys: missing });
  }
  return out;
}

/** Libellés FR pour l’admin plateforme (fiche établissement). */
export const INSTITUTION_MENU_FLAG_LABELS_FR: Record<InstitutionMenuFlagKey, string> = {
  institution_panel: 'Menu : Panneau établissement',
  webinars_enterprise: 'Webinaires entreprise (menu + onglet pilotage + /webinars/enterprise)',
  rh_campaigns: 'Campagnes formation',
  rh_codir: 'CODIR (DRH)',
  rh_competencies: 'Compétences',
  rh_requests: 'Demandes / workflows',
  rh_catalog: 'Catalogue formation',
  integrations_page: 'Page Intégrations (lien RH)',
  tab_webinar_settings: 'Onglet : Paramètres webinaires',
  tab_rh_dashboard: 'Onglet : Pilotage RH',
  tab_infpc: 'Onglet : INFPC (cofinancement)',
  tab_flows: 'Onglet : Parcours EduFlow',
  tab_trash: 'Onglet : Corbeille',
  tab_supports: 'Onglet : Supports',
  tab_categories: 'Onglet : Catégories',
  tab_templates: 'Onglet : Modèles de blocs',
  tab_content_pipeline: 'Onglet : Pipeline contenu',
  tab_designers: 'Onglet : Concepteurs',
  tab_learners: 'Onglet : Apprenants',
  tab_learner_analytics: 'Onglet : Analytics apprenants',
  tab_bi_studio: 'Onglet : Tableaux BI',
  tab_classes: 'Onglet : Classes',
  tab_evaluations: 'Onglet : Évaluations',
  tab_training_sessions: 'Onglet : Sessions (séances de cours)',
  tab_groups: 'Onglet : Groupes',
  tab_services: 'Onglet : Services entreprise',
  tab_branding: 'Onglet : Charte graphique',
  tab_landing: 'Onglet : Page d’accueil portail',
  tab_webhooks: 'Onglet : Webhooks',
  tab_scorm: 'Onglet : SCORM',
  tab_xapi: 'Onglet : xAPI',
  tab_mcp: 'Onglet : MCP',
  tab_sirh: 'Onglet : SIRH',
  learner_public_formations_education: 'Éducation : « Formations publiques » visibles par les élèves',
};

/**
 * Profil « Éducation » (écoles, réseaux d'écoles, ministères) : menus scolaires ouverts, modules RH coupés.
 * Aligné avec `EDUCATION_MENU_PRESET` côté backend (`constants/education-menu-preset.ts`).
 * Indépendamment de ces drapeaux, `lib/institution-segment.ts` masque toujours les modules RH en éducation.
 */
export const EDUCATION_MENU_PRESET: Readonly<Partial<Record<InstitutionMenuFlagKey, boolean>>> = Object.freeze({
  tab_classes: true,
  tab_evaluations: true,
  tab_learners: true,
  tab_learner_analytics: true,
  tab_groups: true,
  tab_training_sessions: true,
  learner_public_formations_education: false,
  /** Écran de traitement des « Demandes de contenus » (proposé dans l’en-tête de l’espace établissement). */
  rh_requests: true,
  rh_codir: false,
  rh_campaigns: false,
  rh_competencies: false,
  rh_catalog: false,
  webinars_enterprise: false,
  integrations_page: false,
  tab_rh_dashboard: false,
  tab_infpc: false,
  tab_sirh: false,
  tab_services: false,
  tab_content_pipeline: false,
});

/** Applique le profil Éducation à un état complet de drapeaux (utilisé pour préremplir les formulaires). */
export function applyEducationMenuPreset(
  expanded: Record<InstitutionMenuFlagKey, boolean>,
): Record<InstitutionMenuFlagKey, boolean> {
  return { ...expanded, ...(EDUCATION_MENU_PRESET as Record<InstitutionMenuFlagKey, boolean>) };
}

/** Drapeaux désactivés tant qu’ils ne sont pas explicitement à `true` (options à activer volontairement). */
const MENU_FLAGS_DEFAULT_OFF: ReadonlySet<string> = new Set<string>([
  INSTITUTION_MENU_FLAGS.learner_public_formations_education,
]);

export function isInstitutionMenuEnabled(
  flags: Record<string, unknown> | null | undefined,
  key: string,
): boolean {
  const defaultValue = !MENU_FLAGS_DEFAULT_OFF.has(key);
  if (flags == null || typeof flags !== 'object') return defaultValue;
  const v = flags[key];
  if (v === undefined) return defaultValue;
  return v !== false;
}

/** État complet pour l’admin : toutes les clés explicites (défaut activé). */
export function expandInstitutionMenuFlags(
  raw: Record<string, unknown> | null | undefined,
): Record<InstitutionMenuFlagKey, boolean> {
  const out = {} as Record<InstitutionMenuFlagKey, boolean>;
  for (const k of ALL_INSTITUTION_MENU_FLAG_KEYS) {
    out[k] = isInstitutionMenuEnabled(raw, k);
  }
  return out;
}

export function collapseInstitutionMenuFlags(
  expanded: Record<InstitutionMenuFlagKey, boolean>,
): Record<string, boolean> {
  const o: Record<string, boolean> = {};
  for (const k of ALL_INSTITUTION_MENU_FLAG_KEYS) {
    o[k] = expanded[k] !== false;
  }
  return o;
}
